import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, ChefHat, UtensilsCrossed, Clock } from 'lucide-react';

const OrderStatusTracker = ({ orders = [] }) => {
  const { t } = useLanguage();

  const statusConfig = {
    received: {
      icon: CheckCircle,
      color: 'bg-[#E9C46A]',
      textColor: 'text-[#2D241D]',
      label: t('received')
    },
    preparing: {
      icon: ChefHat,
      color: 'bg-[#E16833]',
      textColor: 'text-white',
      label: t('preparing')
    },
    ready: {
      icon: UtensilsCrossed,
      color: 'bg-[#2D6A4F]',
      textColor: 'text-white',
      label: t('ready')
    },
    served: {
      icon: CheckCircle,
      color: 'bg-[#0A422D]',
      textColor: 'text-white',
      label: t('served')
    }
  };

  const getStatusStep = (status) => {
    const steps = ['received', 'preparing', 'ready', 'served'];
    return steps.indexOf(status);
  };

  // Filter active orders (not served and not fully paid)
  const activeOrders = orders.filter(order => 
    order.status !== 'served' || !order.is_fully_paid
  );

  if (activeOrders.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-[#E5DCD3] p-4 mb-4" data-testid="order-status-tracker">
      <h3 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
        <Clock className="w-5 h-5 text-[#E16833]" />
        {t('order_status')}
      </h3>

      {activeOrders.map(order => {
        const currentStep = getStatusStep(order.status);
        const StatusIcon = statusConfig[order.status]?.icon || CheckCircle;

        return (
          <div key={order.id} className="mb-4 last:mb-0" data-testid={`order-${order.id}`}>
            {/* Status Progress */}
            <div className="flex items-center justify-between mb-3">
              {['received', 'preparing', 'ready'].map((status, idx) => {
                const isActive = idx <= currentStep;
                const config = statusConfig[status];
                const Icon = config.icon;

                return (
                  <React.Fragment key={status}>
                    <div className={`flex flex-col items-center ${isActive ? 'opacity-100' : 'opacity-40'}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isActive ? config.color : 'bg-[#E5DCD3]'}`}>
                        <Icon className={`w-5 h-5 ${isActive ? config.textColor : 'text-[#6E5E52]'}`} />
                      </div>
                      <span className="text-xs mt-1 font-medium text-[#6E5E52]">{config.label}</span>
                    </div>
                    {idx < 2 && (
                      <div className={`flex-1 h-1 mx-2 rounded ${idx < currentStep ? 'bg-[#2D6A4F]' : 'bg-[#E5DCD3]'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Order Items Summary */}
            <div className="bg-[#F3EDE5] rounded-xl p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#6E5E52]">
                  {order.items.length} {t('items')}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig[order.status]?.color} ${statusConfig[order.status]?.textColor}`}>
                  {statusConfig[order.status]?.label}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {order.items.slice(0, 3).map((item, idx) => (
                  <span key={idx} className="text-xs text-[#2D241D] bg-white px-2 py-1 rounded-full">
                    {item.quantity}x {item.name}
                  </span>
                ))}
                {order.items.length > 3 && (
                  <span className="text-xs text-[#6E5E52]">
                    +{order.items.length - 3} more
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OrderStatusTracker;
